import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock, FaPaperPlane, FaFacebookF, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import SEO from '../../components/SEO';
import api from '../../services/api';
import './contact.css';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const isArabic = language === 'ar';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Envoyer les données au backend via l'API configurée
      const response = await api.createContact(formData);
      console.log('Message envoyé avec succès:', response.data);
      setFormSubmitted(true);
      
      // Réinitialiser le formulaire après 3 secondes
      setTimeout(() => {
        setFormSubmitted(false);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setSubmitError(isArabic ? 'حدث خطأ أثناء إرسال الرسالة. يرجى المحاولة مرة أخرى.' : 'Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.2, 
        duration: 0.5 
      } 
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Contact info data
  const contactLocations = {
    sousse: {
      title: isArabic ? "سوسة" : "Sousse",
      address: isArabic 
        ? "العنوان: عمارة حبيبة، شارع جوهرة الساحل - خزامة - سوسة" 
        : "Adresse: Immeuble Habiba, Rue Jawharat Essahel - Khezama - Sousse",
      phone: "56069069",
      mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3234.9518550097186!2d10.608369315254793!3d35.84675167931174!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x130275759ac9d10d%3A0x698e3915c366b357!2sDispositif%20Nouvelle%20Chance%20-%20DNC%20Sousse!5e0!3m2!1sfr!2stn!4v1647883772900!5m2!1sfr!2stn"
    },
    kairouan: {
      title: isArabic ? "القيروان" : "Kairouan",
      address: isArabic 
        ? "العنوان: شارع طارق ابن زياد - القيروان" 
        : "Adresse: Rue Tarek Ibn Zied - Kairouan",
      phone: "51022929",
      mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d820.0871263420542!2d10.095197829242636!3d35.67270739876606!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12fdc5a571bd48c5%3A0x4e2b2d9f4e3e1be2!2sInstitut%20Europ%C3%A9en%20de%20Coop%C3%A9ration%20et%20de%20D%C3%A9veloppement%20Tunisie%20-%20Bureau%20Kairouan!5e0!3m2!1sfr!2stn!4v1647883850800!5m2!1sfr!2stn"
    }
  };

  const socialLinks = {
    facebook: "https://www.facebook.com/dnc.tunisie",
    instagram: "https://www.instagram.com/dnc.tn",
    linkedin: "https://www.linkedin.com/company/iecd-tunisie"
  };

  return (
    <div className={`contact-page ${isArabic ? 'rtl' : ''}`}>
      <SEO 
        title={isArabic ? 'اتصل بنا - DNC تونس' : 'Contact - Dispositif National Nouvelle Chance'}
        description={isArabic ? 'تواصل معنا في سوسة والقيروان. نحن هنا لمساعدتك' : 'Contactez-nous à Sousse et Kairouan. Nous sommes là pour vous aider'}
        url="/contact"
        type="website"
      />
      <motion.section 
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="contact-section mt-8"
      >
        <div className="container">
          <motion.div variants={itemVariants} className="section-header">
            <h1 className="section-title mt-8">{t('contact.title')}</h1>
            <p className="section-subtitle">{t('contact.subtitle')}</p>
          </motion.div>

          <div className="contact-content">
            {/* Form Section */}
            <motion.div variants={itemVariants} className="contact-form-container">
              <div className="form-card">
                {!formSubmitted ? (
                  <>
                    <h2 className="form-title">{t('contact.sendMessage')}</h2>
                    <form onSubmit={handleSubmit} className="contact-form">
                      <div className="form-grid">
                        <div className="form-group">
                          <label htmlFor="name" className="form-label">{t('contact.form.name')}</label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="form-input"
                            placeholder={t('contact.form.name')}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="email" className="form-label">{t('contact.form.email')}</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="form-input"
                            placeholder={t('contact.form.email')}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="subject" className="form-label">{t('contact.form.subject')}</label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="form-input"
                          placeholder={t('contact.form.subject')}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="message" className="form-label">{t('contact.form.message')}</label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="5"
                          required
                          className="form-textarea"
                          placeholder={t('contact.form.message')}
                        ></textarea>
                      </div>
                      <button type="submit" className="submit-btn" disabled={isSubmitting}>
                        <FaPaperPlane className="btn-icon" />
                        {t('contact.form.send')}
                      </button>
                      {submitError && <p className="error-message">{submitError}</p>}
                    </form>
                  </>
                ) : (
                  <div className="success-message">
                    <h2>{t('contact.form.success.title')}</h2>
                    <p>{t('contact.form.success.message')}</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Social Links */}
            <motion.div variants={itemVariants} className="social-section">
              <div className="social-card">
                <h2 className="social-title">{t('contact.followUs')}</h2>
                <div className="social-icons-large">
                  <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="social-icon-large">
                    <FaFacebookF />
                    <span>Facebook</span>
                  </a>
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="social-icon-large">
                    <FaInstagram />
                    <span>Instagram</span>
                  </a>
                  <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="social-icon-large">
                    <FaLinkedinIn />
                    <span>LinkedIn</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Location Cards */}
          <motion.div variants={containerVariants} className="location-cards">
            {/* Sousse Location */}
            <motion.div variants={itemVariants} className="location-card">
              <h2 className="location-title">{contactLocations.sousse.title}</h2>
              <div className="location-info">
                <div className="info-row">
                  <FaMapMarkerAlt className="info-icon" />
                  <p>{contactLocations.sousse.address}</p>
                </div>
                <a href={`tel:${contactLocations.sousse.phone}`} className="info-row info-row-link">
                  <FaPhoneAlt className="info-icon" />
                  <p>{contactLocations.sousse.phone}</p>
                </a>
                <a href="mailto:dispositifnouvellechance@gmail.com" className="info-row info-row-link">
                  <FaEnvelope className="info-icon" />
                  <p>dispositifnouvellechance@gmail.com</p>
                </a>
                <div className="info-row">
                  <FaClock className="info-icon" />
                  <p>{t('contact.info.hours.content')}</p>
                </div>
              </div>
              <div className="map-container">
                <iframe 
                  src={contactLocations.sousse.mapUrl}
                  width="100%" 
                  height="300" 
                  style={{ border: 0 }} 
                  allowFullScreen="" 
                  loading="lazy"
                  title="Sousse DNC Location"
                ></iframe>
              </div>
            </motion.div>

            {/* Kairouan Location */}
            <motion.div variants={itemVariants} className="location-card">
              <h2 className="location-title">{contactLocations.kairouan.title}</h2>
              <div className="location-info">
                <div className="info-row">
                  <FaMapMarkerAlt className="info-icon" />
                  <p>{contactLocations.kairouan.address}</p>
                </div>
                <a href={`tel:${contactLocations.kairouan.phone}`} className="info-row info-row-link">
                  <FaPhoneAlt className="info-icon" />
                  <p>{contactLocations.kairouan.phone}</p>
                </a>
                <a href="mailto:dispositifnouvellechance@gmail.com" className="info-row info-row-link">
                  <FaEnvelope className="info-icon" />
                  <p>dispositifnouvellechance@gmail.com</p>
                </a>
                <div className="info-row">
                  <FaClock className="info-icon" />
                  <p>{t('contact.info.hours.content')}</p>
                </div>
              </div>
              <div className="map-container">
                <iframe 
                  src={contactLocations.kairouan.mapUrl}
                  width="100%" 
                  height="300" 
                  style={{ border: 0 }} 
                  allowFullScreen="" 
                  loading="lazy"
                  title="Kairouan DNC Location"
                ></iframe>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Contact;
